import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReplayIcon from '@mui/icons-material/Replay';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';
import { useColorMode, useColors } from '../../theme/ColorModeContext';
import { finishTechQuizAttempt, startTechQuizAttempt, TechQuizShare } from '../../api/Api';
import {
    buildQuiz,
    ChoiceKey,
    DIFFICULTY_LABELS,
    DIFFICULTY_ORDER,
    formatClock,
    PreparedQuestion,
    QuizDifficulty,
    QUESTIONS_PER_DIFFICULTY,
    QUIZ_DURATION_MIN,
    QUIZ_DURATION_MS,
    QUIZ_TOTAL,
    QuizQuestion,
} from './quizEngine';
import ResultSummary from './ResultSummary';
import ShareResult from './ShareResult';
import { shareText } from './share';

type Phase = 'intro' | 'quiz' | 'results';

const TOTAL = QUIZ_TOTAL;

/**
 * Lightweight card surface with NO `backdrop-filter`: the blur on `GlowCard`
 * forces a full re-composite on every hover/click inside it. Used for the
 * interactive question card and the review rows; `GlowCard` is kept only for the
 * static intro / score cards that never repaint mid-interaction.
 */
const Panel = ({
    children,
    accent,
    sx,
}: {
    children: React.ReactNode;
    accent?: boolean;
    sx?: SxProps<Theme>;
}) => {
    const colors = useColors();
    const { mode } = useColorMode();
    return (
        <Box
            sx={{
                background:
                    mode === 'dark'
                        ? 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02)), #0d0d15'
                        : '#ffffff',
                border: `1px solid ${accent ? colors.border.accent : colors.border.subtle}`,
                borderRadius: '8px',
                boxShadow: mode === 'dark' ? '0 10px 34px rgba(0,0,0,0.28)' : '0 8px 28px rgba(0,0,0,0.06)',
                ...(sx as any),
            }}
        >
            {children}
        </Box>
    );
};

type OptionState = 'idle' | 'selected' | 'correct' | 'wrong' | 'dim';

/** A single answer row. Memoized so selecting an option re-renders only the changed rows. */
const OptionRow = React.memo(
    ({
        optionKey,
        text,
        state,
        interactive,
        onSelect,
    }: {
        optionKey: ChoiceKey;
        text: string;
        state: OptionState;
        interactive: boolean;
        onSelect?: (key: ChoiceKey) => void;
    }) => {
        const colors = useColors();

        const tone =
            state === 'selected'
                ? colors.accent.cyan
                : state === 'correct'
                    ? colors.accent.green
                    : state === 'wrong'
                        ? colors.accent.red
                        : colors.border.subtle;
        const active = state === 'selected' || state === 'correct' || state === 'wrong';

        const activate = () => {
            if (interactive && onSelect) onSelect(optionKey);
        };

        return (
            <Box
                onClick={activate}
                role={interactive ? 'button' : undefined}
                tabIndex={interactive ? 0 : undefined}
                onKeyDown={
                    interactive
                        ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  activate();
                              }
                          }
                        : undefined
                }
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    p: '13px 15px',
                    borderRadius: '8px',
                    cursor: interactive ? 'pointer' : 'default',
                    border: `1px solid ${tone}`,
                    backgroundColor: active ? `${tone}1a` : 'transparent',
                    opacity: state === 'dim' ? 0.5 : 1,
                    transition: 'border-color 0.12s ease, background-color 0.12s ease',
                    '&:hover': interactive ? { borderColor: colors.accent.cyan } : undefined,
                }}
            >
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                        borderRadius: '6px',
                        display: 'grid',
                        placeItems: 'center',
                        fontFamily: '"Share Tech Mono", monospace',
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        textTransform: 'uppercase',
                        color: colors.bg.primary,
                        backgroundColor: active ? tone : colors.text.muted,
                    }}
                >
                    {optionKey}
                </Box>
                <Typography
                    sx={{
                        color: active ? colors.text.primary : colors.text.secondary,
                        flexGrow: 1,
                        fontSize: '0.92rem',
                    }}
                >
                    {text}
                </Typography>
                {state === 'correct' && <CheckCircleIcon sx={{ color: colors.accent.green, fontSize: '1.15rem' }} />}
                {state === 'wrong' && <CancelIcon sx={{ color: colors.accent.red, fontSize: '1.15rem' }} />}
            </Box>
        );
    },
);
OptionRow.displayName = 'OptionRow';

/**
 * Countdown pill. Owns its own per-second tick so the parent quiz screen does NOT
 * re-render every second. Fires `onExpire` once when the deadline passes. Mount
 * with `key={deadline}` so a retake gets a fresh instance.
 */
const QuizTimer = React.memo(({ deadline, onExpire }: { deadline: number; onExpire: () => void }) => {
    const colors = useColors();
    const [now, setNow] = useState(() => Date.now());
    const firedRef = useRef(false);

    useEffect(() => {
        const tick = () => setNow(Date.now());
        const id = window.setInterval(tick, 1000);
        const onVisible = () => {
            if (document.visibilityState === 'visible') tick();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    const remaining = Math.max(0, deadline - now);

    useEffect(() => {
        if (remaining <= 0 && !firedRef.current) {
            firedRef.current = true;
            onExpire();
        }
    }, [remaining, onExpire]);

    const urgent = remaining <= 60 * 1000;
    const warn = remaining <= 5 * 60 * 1000;
    const tone = urgent ? colors.accent.red : warn ? colors.difficulty.medium : colors.text.secondary;

    return (
        <Box
            sx={{ display: 'flex', alignItems: 'center', gap: '5px', color: tone }}
            role="timer"
            aria-label={`Time remaining ${formatClock(remaining)}`}
        >
            <AccessTimeRoundedIcon sx={{ fontSize: '1rem' }} />
            <Typography
                sx={{
                    fontFamily: '"Share Tech Mono", monospace',
                    fontSize: '0.82rem',
                    fontWeight: urgent ? 700 : 500,
                    color: 'inherit',
                    letterSpacing: '0.04em',
                }}
            >
                {formatClock(remaining)}
            </Typography>
        </Box>
    );
});
QuizTimer.displayName = 'QuizTimer';

interface TechQuizProps {
    /** Stable slug used by the attempt API (e.g. "canton-daml"). */
    slug: string;
    /** Page title, e.g. "Canton & DAML Quiz". */
    title: string;
    /** Short topic name, e.g. "Canton & DAML". */
    topic: string;
    /** The 100-question bank for this quiz. */
    questions: QuizQuestion[];
    /** Optional JWT, sent only when a signed-in user takes the quiz. */
    token?: string;
}

const TechQuiz = ({ slug, title, topic, questions, token }: TechQuizProps) => {
    const colors = useColors();

    const [phase, setPhase] = useState<Phase>('intro');
    const [quiz, setQuiz] = useState<PreparedQuestion[]>([]);
    const [current, setCurrent] = useState(0);
    const [answers, setAnswers] = useState<Record<number, ChoiceKey>>({});

    // Timing: the quiz is always timed; QUIZ_DURATION_MIN is the only mode.
    const [deadline, setDeadline] = useState<number | null>(null);
    const [startedAt, setStartedAt] = useState(0);
    const [finishedAt, setFinishedAt] = useState(0);
    const [timedOut, setTimedOut] = useState(false);

    // Best-effort attempt telemetry (see api/Api.ts). Never blocks the UI.
    const attemptIdRef = useRef<string | null>(null);
    const attemptStartRef = useRef<Promise<string | null> | null>(null);
    const resultPostedRef = useRef(false);
    // Bumped on every (re)start so a late response from an earlier run is ignored.
    const runRef = useRef(0);
    // Share link for the finished run; `undefined` while it is being created.
    const [share, setShare] = useState<TechQuizShare | null | undefined>(undefined);

    const diffColor = useMemo<Record<QuizDifficulty, string>>(
        () => ({
            easy: colors.difficulty.easy,
            medium: colors.difficulty.medium,
            hard: colors.difficulty.veryHard,
        }),
        [colors],
    );

    const start = useCallback(() => {
        const now = Date.now();
        setQuiz(buildQuiz(questions));
        setAnswers({});
        setCurrent(0);
        setStartedAt(now);
        setFinishedAt(0);
        setTimedOut(false);
        setDeadline(now + QUIZ_DURATION_MS);
        setPhase('quiz');
        window.scrollTo({ top: 0 });

        attemptIdRef.current = null;
        resultPostedRef.current = false;
        runRef.current += 1;
        setShare(undefined);
        attemptStartRef.current = startTechQuizAttempt(slug, { total_questions: TOTAL }, token).then((id) => {
            attemptIdRef.current = id;
            return id;
        });
    }, [questions, slug, token]);

    const finish = useCallback((viaTimeout: boolean) => {
        setFinishedAt(Date.now());
        setTimedOut(viaTimeout);
        setPhase('results');
        window.scrollTo({ top: 0 });
    }, []);

    const handleExpire = useCallback(() => finish(true), [finish]);

    const select = useCallback(
        (key: ChoiceKey) => {
            setAnswers((prev) => (prev[current] === key ? prev : { ...prev, [current]: key }));
        },
        [current],
    );

    const goNext = useCallback(() => {
        if (current < TOTAL - 1) {
            setCurrent(current + 1);
            window.scrollTo({ top: 0 });
        } else {
            finish(false);
        }
    }, [current, finish]);

    const goPrev = useCallback(() => {
        setCurrent((c) => Math.max(0, c - 1));
        window.scrollTo({ top: 0 });
    }, []);

    const answeredCount = Object.keys(answers).length;

    const score = useMemo(() => {
        let correct = 0;
        const perTier: Record<QuizDifficulty, { correct: number; total: number }> = {
            easy: { correct: 0, total: 0 },
            medium: { correct: 0, total: 0 },
            hard: { correct: 0, total: 0 },
        };
        quiz.forEach((q, i) => {
            perTier[q.difficulty].total += 1;
            if (answers[i] === q.correctKey) {
                correct += 1;
                perTier[q.difficulty].correct += 1;
            }
        });
        return { correct, perTier };
    }, [quiz, answers]);

    // Post the result once when the quiz reaches the results screen (manual or timer).
    useEffect(() => {
        if (phase !== 'results' || resultPostedRef.current) return;
        resultPostedRef.current = true;

        const raw = finishedAt && startedAt ? finishedAt - startedAt : 0;
        const cappedMs = Math.min(raw, QUIZ_DURATION_MS);
        const payload = {
            score: score.correct,
            score_easy: score.perTier.easy.correct,
            score_medium: score.perTier.medium.correct,
            score_hard: score.perTier.hard.correct,
            duration_seconds: Math.round(cappedMs / 1000),
            timed_out: timedOut,
        };

        const run = runRef.current;
        const settle = (result: TechQuizShare | null) => {
            if (runRef.current === run) setShare(result);
        };
        const send = (id: string | null) => {
            if (id) finishTechQuizAttempt(slug, id, payload, token).then(settle);
            else settle(null);
        };
        if (attemptIdRef.current) send(attemptIdRef.current);
        else if (attemptStartRef.current) attemptStartRef.current.then(send);
    }, [phase, score, finishedAt, startedAt, timedOut, slug, token]);

    // --- Intro ---
    if (phase === 'intro') {
        return (
            <PageContainer maxWidth="md">
                <CyberpunkTitle title={title} mb="24px" />
                <GlowCard glowOnHover={false} gradientBorder>
                    <Box sx={{ p: { xs: '24px', md: '36px' } }}>
                        <Typography sx={{ color: colors.text.secondary, lineHeight: 1.7, mb: 2.5 }}>
                            Test your knowledge of {topic}. Every run pulls{' '}
                            <Box component="span" sx={{ color: colors.text.primary, fontWeight: 700 }}>
                                {TOTAL} random questions
                            </Box>{' '}
                            from a bank of 100: {QUESTIONS_PER_DIFFICULTY} easy, {QUESTIONS_PER_DIFFICULTY} average,
                            and {QUESTIONS_PER_DIFFICULTY} very hard, shown from easiest to hardest. Answer choices are
                            shuffled each time.
                        </Typography>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                                gap: '12px',
                                mb: 3,
                            }}
                        >
                            {DIFFICULTY_ORDER.map((d) => (
                                <Box
                                    key={d}
                                    sx={{
                                        p: '14px 16px',
                                        borderRadius: '8px',
                                        border: `1px solid ${colors.border.subtle}`,
                                        backgroundColor: colors.bg.tertiary,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: '"Share Tech Mono", monospace',
                                            fontSize: '0.72rem',
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            color: diffColor[d],
                                            mb: 0.5,
                                        }}
                                    >
                                        {DIFFICULTY_LABELS[d]}
                                    </Typography>
                                    <Typography sx={{ color: colors.text.primary, fontWeight: 700 }}>
                                        {QUESTIONS_PER_DIFFICULTY} questions
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                p: '14px 16px',
                                borderRadius: '8px',
                                border: `1px solid ${colors.border.accent}`,
                                backgroundColor: colors.bg.tertiary,
                                mb: 3,
                            }}
                        >
                            <AccessTimeRoundedIcon sx={{ color: colors.accent.cyan, flexShrink: 0 }} />
                            <Box>
                                <Typography sx={{ color: colors.text.primary, fontWeight: 700 }}>
                                    {QUIZ_DURATION_MIN}-minute time limit
                                </Typography>
                                <Typography sx={{ color: colors.text.muted, fontSize: '0.82rem' }}>
                                    You get {QUIZ_DURATION_MIN} minutes for all {TOTAL} questions. The quiz submits itself
                                    automatically when the timer hits zero.
                                </Typography>
                            </Box>
                        </Box>

                        <Typography sx={{ color: colors.text.muted, fontSize: '0.85rem', mb: 3 }}>
                            This quiz is for practice only. It does not require sign-in and does not add points to your
                            Web3Pwn profile. Your score is shown at the end.
                        </Typography>

                        <Button
                            variant="contained"
                            size="large"
                            onClick={start}
                            endIcon={<ArrowForwardIcon />}
                            sx={{ fontFamily: '"Orbitron", sans-serif', fontWeight: 700 }}
                        >
                            {`Start ${QUIZ_DURATION_MIN}-minute quiz`}
                        </Button>
                    </Box>
                </GlowCard>
            </PageContainer>
        );
    }

    // --- Results ---
    if (phase === 'results') {
        const rawElapsed = finishedAt && startedAt ? finishedAt - startedAt : 0;
        const elapsedMs = Math.min(rawElapsed, QUIZ_DURATION_MS);
        // Say up front what the shared page reveals, before anyone posts it.
        const shareNote = share?.username
            ? `The shared page shows your username, ${share.username}.`
            : 'Shared anonymously. Sign in before starting a quiz to put your username on it.';

        return (
            <PageContainer maxWidth="md">
                <CyberpunkTitle title="Your Results" subtitle={title} mb="24px" />

                <GlowCard glowOnHover={false} gradientBorder sx={{ mb: '24px' }}>
                    <Box sx={{ p: { xs: '28px', md: '40px' }, textAlign: 'center' }}>
                        <ResultSummary
                            correct={score.correct}
                            total={TOTAL}
                            perTier={score.perTier}
                            elapsedMs={elapsedMs}
                            timedOut={timedOut}
                        />

                        <Typography sx={{ color: colors.text.muted, fontSize: '0.82rem', mt: 1.5 }}>
                            Practice quiz. No points were added to any account.
                        </Typography>

                        <ShareResult
                            shareUrl={share ? share.share_url : null}
                            pending={share === undefined}
                            text={shareText(score.correct, TOTAL, title)}
                            note={share ? shareNote : undefined}
                        />

                        <Button
                            variant="contained"
                            size="large"
                            onClick={start}
                            startIcon={<ReplayIcon />}
                            sx={{ mt: 3, fontFamily: '"Orbitron", sans-serif', fontWeight: 700 }}
                        >
                            Take a new quiz
                        </Button>
                    </Box>
                </GlowCard>

                <Typography
                    sx={{
                        fontFamily: '"Orbitron", sans-serif',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: colors.text.secondary,
                        mb: 2,
                    }}
                >
                    Review
                </Typography>

                {quiz.map((q, i) => {
                    const picked = answers[i];
                    const isCorrect = picked === q.correctKey;
                    return (
                        <Panel key={q.id} sx={{ mb: '14px' }}>
                            <Box sx={{ p: { xs: '18px', md: '24px' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', mb: 1.5 }}>
                                    {isCorrect ? (
                                        <CheckCircleIcon sx={{ color: colors.accent.green, mt: '2px' }} />
                                    ) : (
                                        <CancelIcon sx={{ color: colors.accent.red, mt: '2px' }} />
                                    )}
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: '"Share Tech Mono", monospace',
                                                fontSize: '0.68rem',
                                                letterSpacing: '0.1em',
                                                textTransform: 'uppercase',
                                                color: diffColor[q.difficulty],
                                                mb: 0.5,
                                            }}
                                        >
                                            Q{i + 1} &middot; {DIFFICULTY_LABELS[q.difficulty]}
                                        </Typography>
                                        <Typography sx={{ color: colors.text.primary, fontWeight: 700, lineHeight: 1.5 }}>
                                            {q.question}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'grid', gap: '6px', pl: { xs: 0, md: '34px' } }}>
                                    {q.options.map((opt) => {
                                        const state: OptionState =
                                            opt.key === q.correctKey
                                                ? 'correct'
                                                : opt.key === picked
                                                    ? 'wrong'
                                                    : 'idle';
                                        return (
                                            <OptionRow
                                                key={opt.key}
                                                optionKey={opt.key}
                                                text={opt.text}
                                                state={state}
                                                interactive={false}
                                            />
                                        );
                                    })}
                                    {picked === undefined && (
                                        <Typography sx={{ color: colors.text.muted, fontSize: '0.8rem', mt: 0.5 }}>
                                            You skipped this question.
                                        </Typography>
                                    )}
                                </Box>

                                <Typography
                                    sx={{
                                        color: colors.text.secondary,
                                        fontSize: '0.88rem',
                                        lineHeight: 1.6,
                                        mt: 1.5,
                                        pl: { xs: 0, md: '34px' },
                                    }}
                                >
                                    {q.explanation}
                                </Typography>
                            </Box>
                        </Panel>
                    );
                })}
            </PageContainer>
        );
    }

    // --- Quiz ---
    const q = quiz[current];
    const picked = answers[current];
    const isLast = current === TOTAL - 1;

    return (
        <PageContainer maxWidth="md">
            <CyberpunkTitle title={title} mb="20px" />

            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', mb: 0.75 }}>
                    <Typography
                        sx={{
                            fontFamily: '"Share Tech Mono", monospace',
                            fontSize: '0.78rem',
                            color: colors.text.secondary,
                            letterSpacing: '0.08em',
                        }}
                    >
                        Question {current + 1} / {TOTAL}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <Typography
                            sx={{
                                fontFamily: '"Share Tech Mono", monospace',
                                fontSize: '0.78rem',
                                color: colors.text.muted,
                            }}
                        >
                            {answeredCount} answered
                        </Typography>
                        {deadline != null && <QuizTimer key={deadline} deadline={deadline} onExpire={handleExpire} />}
                    </Box>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={((current + 1) / TOTAL) * 100}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: colors.bg.tertiary,
                        '& .MuiLinearProgress-bar': { backgroundColor: colors.accent.cyan },
                    }}
                />
            </Box>

            <Panel accent>
                <Box sx={{ p: { xs: '22px', md: '32px' } }}>
                    <Typography
                        sx={{
                            fontFamily: '"Share Tech Mono", monospace',
                            fontSize: '0.72rem',
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            color: diffColor[q.difficulty],
                            mb: 1.5,
                        }}
                    >
                        {DIFFICULTY_LABELS[q.difficulty]}
                    </Typography>

                    <Typography
                        sx={{
                            color: colors.text.primary,
                            fontFamily: '"Orbitron", sans-serif',
                            fontWeight: 700,
                            fontSize: { xs: '1.05rem', md: '1.2rem' },
                            lineHeight: 1.5,
                            mb: 3,
                        }}
                    >
                        {q.question}
                    </Typography>

                    <Box sx={{ display: 'grid', gap: '10px' }}>
                        {q.options.map((opt) => (
                            <OptionRow
                                key={opt.key}
                                optionKey={opt.key}
                                text={opt.text}
                                state={opt.key === picked ? 'selected' : 'idle'}
                                interactive
                                onSelect={select}
                            />
                        ))}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 3,
                            gap: '12px',
                        }}
                    >
                        <Button variant="text" onClick={goPrev} disabled={current === 0} startIcon={<ArrowBackIcon />}>
                            Previous
                        </Button>
                        <Button
                            variant="contained"
                            onClick={goNext}
                            endIcon={isLast ? undefined : <ArrowForwardIcon />}
                            sx={{ fontFamily: '"Orbitron", sans-serif', fontWeight: 700 }}
                        >
                            {isLast ? 'Finish & see score' : 'Next'}
                        </Button>
                    </Box>
                    {isLast && answeredCount < TOTAL && (
                        <Typography sx={{ color: colors.text.muted, fontSize: '0.8rem', mt: 1.5, textAlign: 'right' }}>
                            {TOTAL - answeredCount} question{TOTAL - answeredCount === 1 ? '' : 's'} still unanswered
                            and will be marked incorrect.
                        </Typography>
                    )}
                </Box>
            </Panel>
        </PageContainer>
    );
};

export default TechQuiz;
